import type { Dispatch, SetStateAction } from "react";
import type { Alpha2Code } from "i18n-iso-countries";
import React from "react";
import type { NavItem } from "../main-nav/navigation-links";
import type { NavLinkKey } from "../main-nav/constants";
import { useNavigation } from "../main-nav/NavigationContext";
import dynamic from "next/dynamic";
import { useCountryStateCookies } from "hooks/cookies/useCountryStateCookies";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import { useStaking } from "utils/staking/useStaking";
import { SubMenuColumn, SubMenuWrapper } from "./style";
import SubMenuItem from "./common/sub-menu-item";
import NavCard from "./common/NavCard";
import { HTMLButtonMainNav } from "components/parts/button";
import { CloseMenu } from "../mobile-nav/style";
import { Col } from "components/layout/Grid";
import { FontSize, LineHeight } from "components/parts/Typography";

import CloseMenuImg from "public/static/images/dark-theme/close-menu-grey.svg";

import { getSubMenuConfig } from "../main-nav/navigation-links";

const FocusTrap = dynamic(() => import("./common/LazyFocusTrap"), {
  ssr: false
});

interface SubMenuProps {
  menuItemName?: NavLinkKey | null;
  setMenuChoice?: Dispatch<NavLinkKey | null>;
  isCloseButtonActive?: boolean;
  setIsCloseButtonActive?: Dispatch<SetStateAction<boolean>>;
}

export const SubMenu = ({
  menuItemName: propMenuItemName,
  setMenuChoice: propSetMenuChoice,
  isCloseButtonActive: propIsCloseButtonActive,
  setIsCloseButtonActive: propSetIsCloseButtonActive
}: SubMenuProps) => {
  const context = useNavigation();
  const menuItemName = propMenuItemName || context.activeMenu;
  const setMenuChoice = propSetMenuChoice || context.setActiveMenu;
  const isCloseButtonActive =
    propIsCloseButtonActive !== undefined
      ? propIsCloseButtonActive
      : context.isCloseButtonActive;
  const setIsCloseButtonActive =
    propSetIsCloseButtonActive || context.setIsCloseButtonActive;
  const t = useTranslations(FILES.COMMON);
  const { country } = useCountryStateCookies();
  const { maxStakingRewardRate } = useStaking();

  if (!menuItemName) return null;

  // Get menu configuration
  const menuConfig = getSubMenuConfig({
    menuName: menuItemName,
    t,
    countryCode: country as Alpha2Code,
    maxStakingRewardRate
  });

  if (!menuConfig) return null;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.target === event.currentTarget && event.key === "Enter") {
      setMenuChoice(null);
      setIsCloseButtonActive(true);
    }
  };

  const renderMenuContent = () => {
    const columnCount = menuConfig.columnCount || 2;
    const columns: NavItem[][] = [];

    // Check if we should use specific column indices or auto-distribute
    if (menuConfig.useSpecificColumns) {
      // Initialize empty columns
      for (let i = 0; i < columnCount; i++) {
        columns.push([]);
      }

      // Sort items into their specified columns
      menuConfig.items.forEach((item) => {
        const targetColumn =
          typeof item.columnIndex === "number" ? item.columnIndex : 0;

        // Ensure column index is valid
        if (targetColumn >= 0 && targetColumn < columnCount) {
          columns[targetColumn].push(item);
        } else {
          // Fallback to first column if invalid column index
          columns[0].push(item);
        }
      });
    } else {
      // Use the original auto-distribution logic
      const itemsPerColumn = Math.ceil(menuConfig.items.length / columnCount);
      for (let i = 0; i < columnCount; i++) {
        const start = i * itemsPerColumn;
        const end = start + itemsPerColumn;
        columns.push(menuConfig.items.slice(start, end));
      }
    }

    return (
      <React.Fragment>
        {/* NavCard component for the menu's hero section */}
        {menuConfig.imageSrc && (
          <NavCard
            path={menuConfig.imagePath || "/"}
            imgSrc={menuConfig.imageSrc}
            bgColor="transparent"
            title={t(menuConfig.title)}
            body={menuConfig.description ? t(menuConfig.description) : ""}
          />
        )}

        {columns.map((column, columnIndex) => (
          <SubMenuColumn key={columnIndex}>
            {/* Column header if configured */}
            {menuConfig.columnConfig?.[columnIndex]?.header && (
              <SubMenuItem
                key={`${menuItemName}-col${columnIndex}-header`}
                title={t(menuConfig.columnConfig[columnIndex].header!)}
                isHeading={true}
                style={{
                  margin: "-40px 0 16px 0", // Use negative top margin to push header above menu items
                  fontWeight: "bold",
                  minHeight: "auto",
                  textTransform: "uppercase",
                  color: "#B3B3B4" // Using the requested gray color
                }}
              />
            )}

            {/* Add space for columns without headers to align content */}
            {column.map((item, itemIndex) => (
              <SubMenuItem
                key={`${menuItemName}-col${columnIndex}-item${itemIndex}`}
                title={item.title ? t(item.title) : undefined}
                description={
                  item.description
                    ? item.description === "stakingDescription"
                      ? t("stakingDescription", { maxStakingRewardRate })
                      : t(item.description)
                    : undefined
                }
                link={
                  item.link
                    ? {
                        href: item.link.href,
                        useReferrerPolicy: item.link.useReferrerPolicy,
                        id: item.link.id
                      }
                    : undefined
                }
                newTab={item.link?.newTab || item.newTab}
                style={
                  item.style || {
                    // Use more compact styling for items in columns with headers
                    minHeight: menuConfig.columnConfig?.[columnIndex]?.header
                      ? "auto"
                      : "70px",
                    margin: menuConfig.columnConfig?.[columnIndex]?.header
                      ? "4px 0"
                      : "0px 0px 24px 0px"
                  }
                }
                customFontSize={FontSize.px22}
                customLineHeight={LineHeight.px22}
                isHeading={item.isHeading}
              />
            ))}
          </SubMenuColumn>
        ))}
      </React.Fragment>
    );
  };

  return (
    <FocusTrap active={true}>
      <div>
        {isCloseButtonActive && (
          <Col justifyContent="flex-end">
            <HTMLButtonMainNav onKeyDown={handleKeyDown}>
              <CloseMenu
                style={{ width: "20px", height: "20px" }}
                src={CloseMenuImg}
                alt="Close"
              />
            </HTMLButtonMainNav>
          </Col>
        )}

        <SubMenuWrapper>{renderMenuContent()}</SubMenuWrapper>
      </div>
    </FocusTrap>
  );
};
